import React from 'react';
import SegmentHeader from './SegmentHeader';
import SegmentFilters from './segment-components/SegmentFilters';
import SegmentOverview from './SegmentOverview';
import SegmentDetail from './SegmentDetail';
import StationMapView from './segment-components/StationMapView';
import SegmentProfile from './segment-components/SegmentProfile';
import StationPerformanceTable from './segment-components/StationPerformanceTable';
import SegmentDemographics from './segment-components/SegmentDemographics';
import SegmentDaypartAnalysis from './segment-components/SegmentDaypartAnalysis';
import AICampaignRecommendations from './AICampaignRecommendations';
import SegmentPerformanceCharts from './segment-components/SegmentPerformanceCharts';
import { log } from '@/utils/logUtils';

const SegmentationExplorerCore = ({ 
  selectedSegment,
  selectedStations = [],
  filters = {},
  activeTab,
  viewType,
  timeFrame,
  categoryFilter,
  customerSatisfaction,
  clvData,
  shareOfWalletData,
  annualFrequencyData,
  demographicsData,
  selectedMetric,
  setSelectedMetric,
  onSegmentSelect,
  onStationSelect,
  onFilterChange,
  onResetFilters,
  onTabChange,
  onViewTypeChange,
  setTimeFrame,
  setCategoryFilter,
  stationData = [],
  trendData
}) => {
  // Create a default empty array for favorite stations
  const favoriteStations = [];
  
  // Create a dummy handler for toggling favorite stations
  const handleToggleFavorite = (stationId) => {
    log('Toggle favorite for station:', stationId);
    // This would normally update state, but we're just providing a stub for now
  };

  // Use the passed trendData or fallback to default if not provided
  const volumeTrendData = trendData || [
    { month: 'Jan', urban: 845, highway: 770, rural: 510, suburban: 640, urbanCafe: 220, highwayCafe: 190, ruralCafe: 150, suburbanCafe: 185 },
    { month: 'Feb', urban: 852, highway: 775, rural: 515, suburban: 645, urbanCafe: 225, highwayCafe: 195, ruralCafe: 155, suburbanCafe: 190 },
    { month: 'Mar', urban: 865, highway: 785, rural: 520, suburban: 650, urbanCafe: 230, highwayCafe: 200, ruralCafe: 160, suburbanCafe: 200 },
    { month: 'Apr', urban: 855, highway: 790, rural: 525, suburban: 655, urbanCafe: 235, highwayCafe: 205, ruralCafe: 165, suburbanCafe: 205 },
    { month: 'May', urban: 870, highway: 795, rural: 530, suburban: 660, urbanCafe: 240, highwayCafe: 210, ruralCafe: 170, suburbanCafe: 210 },
    { month: 'Jun', urban: 880, highway: 800, rural: 540, suburban: 675, urbanCafe: 245, highwayCafe: 215, ruralCafe: 175, suburbanCafe: 215 }
  ];

  const segmentData = {
    urban: {
      id: 'urban',
      name: 'Urban Premium',
      description: 'High-traffic urban locations with premium amenities',
      traffic: 847,
      basketSize: 752,
      conversionRate: 68,
      clv: clvData?.urban || 8.7,
      targetCLV: 8.0,
      peakHours: '7-9 AM',
      nfrSales: 47,
      margin: 0.88,
      shareOfWallet: shareOfWalletData?.urban || 80,
      loyalty: 43,
      avgTransactionValue: 850
    },
    highway: {
      id: 'highway',
      name: 'Highway Transit',
      description: 'Major highway locations serving travelers',
      traffic: 925,
      basketSize: 820,
      conversionRate: 52,
      clv: clvData?.highway || 6.4,
      targetCLV: 6.0,
      peakHours: '10-2 PM',
      nfrSales: 38,
      margin: 0.75,
      shareOfWallet: shareOfWalletData?.highway || 85,
      loyalty: 32,
      avgTransactionValue: 780
    },
    rural: {
      id: 'rural',
      name: 'Rural Standard',
      description: 'Rural locations serving local communities',
      traffic: 625,
      basketSize: 580,
      conversionRate: 58,
      clv: clvData?.rural || 5.3,
      targetCLV: 5.0,
      peakHours: '5-7 PM',
      nfrSales: 28,
      margin: 0.81,
      shareOfWallet: shareOfWalletData?.rural || 54,
      loyalty: 47,
      avgTransactionValue: 520
    },
    suburban: {
      id: 'suburban',
      name: 'Suburban Mixed',
      description: 'Suburban locations with mixed customer base',
      traffic: 725,
      basketSize: 650,
      conversionRate: 62,
      clv: clvData?.suburban || 7.1,
      targetCLV: 7.0,
      peakHours: '8-10 AM',
      nfrSales: 33,
      margin: 0.79,
      shareOfWallet: shareOfWalletData?.suburban || 65,
      loyalty: 38,
      avgTransactionValue: 650
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <SegmentHeader 
        activeTab={activeTab}
        onTabChange={onTabChange}
        viewType={viewType}
        onViewTypeChange={onViewTypeChange}
      />
      
      {activeTab === 'overview' && (
        <SegmentOverview 
          onSelectSegment={onSegmentSelect}
          filters={filters}
          onFilterChange={onFilterChange}
          onResetFilters={onResetFilters}
          customerSatisfaction={customerSatisfaction}
          clvData={clvData}
          shareOfWalletData={shareOfWalletData}
          annualFrequencyData={annualFrequencyData}
          trendData={volumeTrendData}
        />
      )}
      
      {activeTab === 'detail' && (
        <div className="px-6 py-4">
          <SegmentDetail 
            segmentId={selectedSegment}
            selectedStations={selectedStations}
            filters={filters}
            viewType={viewType}
            timeFrame={timeFrame}
            categoryFilter={categoryFilter}
            customerSatisfaction={customerSatisfaction}
            clvData={clvData}
            demographicsData={demographicsData}
            onStationSelectionChange={onStationSelect}
            onViewTypeChange={onViewTypeChange}
            setTimeFrame={setTimeFrame}
            setCategoryFilter={setCategoryFilter}
            onTabChange={onTabChange}
            onSegmentSelect={onSegmentSelect}
            stationData={stationData}
            trendData={volumeTrendData}
          />
        </div>
      )}
    </div>
  );
};

export default SegmentationExplorerCore;
